import React, { useEffect, useState } from 'react';
import { usePresence } from '../../context/PresenceContext';
import { MessageSquare, X } from 'lucide-react';

/**
 * Subtle notification toast that appears briefly in the corner
 * Non-intrusive - auto-dismisses after 4 seconds
 */
const NotificationToast = () => {
  const { pendingNotifications, dismissNotification } = usePresence();
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    // Show new notifications with animation
    if (pendingNotifications.length > visibleNotifications.length) {
      const newNotifications = pendingNotifications.slice(visibleNotifications.length);
      setVisibleNotifications(pendingNotifications);

      // Auto-dismiss each notification after 4 seconds
      newNotifications.forEach((_, index) => {
        const actualIndex = visibleNotifications.length + index;
        setTimeout(() => {
          dismissNotification(actualIndex);
        }, 4000);
      });
    }
  }, [pendingNotifications, visibleNotifications.length, dismissNotification]);

  // Keep only the last 3 notifications visible
  const displayNotifications = pendingNotifications.slice(-3);

  if (displayNotifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {displayNotifications.map((notification, index) => (
        <div
          key={`${notification.timestamp}-${index}`}
          className="pointer-events-auto animate-slide-in-right bg-slate-800/95 backdrop-blur border border-cyan-500/30 rounded-lg shadow-xl p-3 max-w-xs"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageSquare size={16} className="text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {notification.senderName}
              </p>
              {notification.preview && (
                <p className="text-xs text-slate-400 truncate mt-0.5">
                  {notification.preview}
                </p>
              )}
            </div>
            <button
              onClick={() => dismissNotification(pendingNotifications.length - displayNotifications.length + index)}
              className="text-slate-500 hover:text-white transition p-1 -mr-1 -mt-1"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationToast;
